// Copyright 2019 Drivetrain Hub LLC
// Custom validators for el forms.

// region HELPERS

function isDefined(val) {
  // https://stackoverflow.com/questions/154059/how-to-check-empty-undefined-null-string-in-javascript
  return val !== "" && val !== null;
}

function isNumber(val) {
  // Is a number.
  return !isNaN(val);
}

function isNumberInRange(val, min, max) {
  // Is a number AND is a float within min and max values.
  const valFloat = parseFloat(val);
  return !isNaN(val) && (max >= valFloat && valFloat >= min)
}

export function isPositiveNumber(val) {
  // Is a number AND is a float greater than zero.
  return !isNaN(val) && parseFloat(val) > 0;
}

function isZeroPositiveNumber(val) {
  // Is a number AND is a float greater than or equal to zero.
  return !isNaN(val) && parseFloat(val) >= 0;
}

function isPositiveInteger(val) {
  // Is an integer greater than zero.
  // https://stackoverflow.com/questions/10834796/validate-that-a-string-is-a-positive-integer
  return /^\+?[1-9]\d*$/.test(val);
}

// endregion

// region VALIDATORS

const validateNumber = (rule, value, callback) => {
  // Validate value is a number.
  // Empty string and null skip validation, otherwise this validator would not work with optional fields.

  if (isDefined(value)) {
    if (isNumber(value)) {
      callback();  // valid
    } else {
      callback(new Error("Not a number"));  // invalid
    }
  }
};

const validateNumberRange = (min, max) => {
  // Validate value is within a range of numbers.
  // Empty string and null skip validation, otherwise this validator would not work with optional fields.

  return (rule, value, callback) => {
    if (isDefined(value)) {
      if (isNumberInRange(value, min, max)) {
        callback();  // valid
      } else {
        callback(new Error(`Not a number within ${min} to ${max}`));  // invalid
      }
    }
  }
};

const validatePositiveNumber = (rule, value, callback) => {
  // Validate value is a positive non-zero number.
  // Empty string and null skip validation, otherwise this validator would not work with optional fields.

  if (isDefined(value)) {
    if (isPositiveNumber(value)) {
      callback();  // valid
    } else {
      callback(new Error("Not a positive number"));  // invalid
    }
  }
};

const validateZeroPositiveNumber = (rule, value, callback) => {
  // Validate value is a number greater than or equal to zero.
  // Empty string skips validation, otherwise this validator would not work with optional fields.

  if (isDefined(value)) {
    if (isZeroPositiveNumber(value)) {
      callback();  // valid
    } else {
      callback(new Error("Not a positive number or zero"));  // invalid
    }
  }
};

const validatePositiveInteger = (rule, value, callback) => {
  // Validate value is a positive non-zero integer.
  // Empty string skips validation, otherwise this validator would not work with optional fields.

  if (isDefined(value)) {
    if (isPositiveInteger(value)) {
      callback();  // valid
    } else {
      callback(new Error("Not a positive integer"))  // invalid
    }
  }
};

const validateUniqueProp = (arr, prop, rule, value, callback) => {
  // validate uniqueness of version prop

  let unique = true;  // initialize

  for (let i = 0; i < arr.length; i++) {
    if (value === arr[i][prop]) {
      unique = false;
      break
    }
  }

  if (!unique) {
    callback(new Error('Already exists'));  // invalid
  } else {
    callback();  // valid
  }
};

export const validators = {
  validateNumber: validateNumber,
  validatePositiveNumber: validatePositiveNumber,
  validateZeroPositiveNumber: validateZeroPositiveNumber,
  validatePositiveInteger: validatePositiveInteger,
  validateUniqueProp: validateUniqueProp,
};

// endregion

// region RULES

const TRIGGER = ['blur',];  // 'change'
const ruleRequired = {required: true, message: 'Required', trigger: TRIGGER};
const ruleOptional = {required: false};
const ruleNumber = {validator: validateNumber, message: 'Invalid number', trigger: TRIGGER};
const rulePositiveNumber = {validator: validatePositiveNumber, message: 'Invalid positive number', trigger: TRIGGER};
const rulePositiveInteger = {validator: validatePositiveInteger, message: 'Invalid positive integer', trigger: TRIGGER};
const ruleZeroPositiveNumber = {validator: validateZeroPositiveNumber, message: 'Invalid positive number', trigger: TRIGGER};
const ruleNumberRange = (min, max) => {
  return {validator: validateNumberRange(min, max), message: `Invalid number from ${min} to ${max}`, trigger: TRIGGER}
};

const requiredNumber = [ruleRequired, ruleNumber];
const optionalNumber = [ruleOptional, ruleNumber];
const requiredPositiveNumber = [ruleRequired, rulePositiveNumber];
const optionalPositiveNumber = [ruleOptional, rulePositiveNumber];
const requiredPositiveInteger = [ruleRequired, rulePositiveInteger];
const requiredZeroPositiveNumber = [ruleRequired, ruleZeroPositiveNumber];
const requiredNumber0To100 = [ruleRequired, ruleNumberRange(0, 100)];

export const rules = {
  required: ruleRequired,
  requiredNumber: requiredNumber,
  optionalNumber: optionalNumber,
  requiredPositiveNumber: requiredPositiveNumber,
  optionalPositiveNumber: optionalPositiveNumber,
  requiredPositiveInteger: requiredPositiveInteger,
  requiredZeroPositiveNumber: requiredZeroPositiveNumber,
  requiredNumber0To100: requiredNumber0To100,
};

// endregion
